import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Slider,
  IconButton,
  Button,
  Divider,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { PageRange } from "../../../Models/evaluation";
import colors from "../../../Constants/colors";
import { useTranslation } from "react-i18next";

interface Props {
  open: boolean;
  pageCount?: number;
  initialRanges: PageRange[];
  onSave: (ranges: PageRange[]) => void;
  onClose: () => void;
}

const PageRangesModal: React.FC<Props> = ({
  open,
  pageCount,
  initialRanges,
  onSave,
  onClose,
}) => {
  const maxPages = pageCount && pageCount > 0 ? pageCount : 100;
  const [sliderValue, setSliderValue] = useState<number[]>([1, maxPages]);
  const [ranges, setRanges] = useState<PageRange[]>([...initialRanges]);
  const { t } = useTranslation("evaluationPage");

  const addRange = () => {
    const [start, end] = sliderValue;
    if (start <= end) {
      setRanges((prev) => [...prev, { start_page: start, end_page: end }]);
    }
  };

  const deleteRange = (idx: number) =>
    setRanges((prev) => prev.filter((_, i) => i !== idx));

  const handleSave = () => {
    const clean = ranges.map((r) => ({
      start_page: Math.max(1, r.start_page),
      end_page: Math.min(maxPages, r.end_page),
    }));
    onSave(clean);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle
        sx={{
          textAlign: "center",
          fontWeight: 600,
          color: colors.primary_dark,
        }}
      >
        {t("evaluationPage.pageRange")}
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 2, color: colors.gray700 }}>
          {t("evaluationPage.range")}: {sliderValue[0]} – {sliderValue[1]}
        </Typography>

        <Slider
          value={sliderValue}
          min={1}
          max={maxPages}
          onChange={(_, v) => setSliderValue(v as number[])}
          valueLabelDisplay="auto"
          onPointerDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
          sx={{
            "& .MuiSlider-rail": {
              backgroundColor: colors.gray200,
              opacity: 0.8,
            },
            "& .MuiSlider-track": {
              backgroundColor: colors.accent,
            },
            "& .MuiSlider-thumb": {
              borderColor: colors.accent,
              backgroundColor: colors.white,
            },
            height: 6,
          }}
        />

        <Button
          startIcon={<AddIcon />}
          onClick={addRange}
          sx={{
            mt: 3,
            borderRadius: 2,
            borderColor: colors.gray200,
            color: colors.primary_dark,
            "&:hover": {
              bgcolor: colors.gray50,
              borderColor: colors.primary_dark,
            },
          }}
          variant="outlined"
        >
          {t("evaluationPage.onePageRange")}
        </Button>

        {ranges.length > 0 && (
          <>
            <Divider sx={{ my: 3, borderColor: colors.gray200 }} />
            {ranges.map((r, idx) => (
              <Box
                key={`${r.start_page}-${r.end_page}-${idx}`}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1 }}
              >
                <Box display="flex" alignItems="center">
                  <Typography
                    sx={{
                      minWidth: 24,
                      mr: 1.1,
                      fontWeight: 600,
                      color: colors.gray700,
                    }}
                  >
                    {idx + 1}.
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.gray700 }}>
                    {t("evaluationPage.range")}: {r.start_page} – {r.end_page}
                  </Typography>
                </Box>

                <IconButton
                  size="small"
                  onClick={() => deleteRange(idx)}
                  sx={{ "&:hover": { bgcolor: colors.red50 } }}
                >
                  <DeleteIcon fontSize="small" sx={{ color: colors.red600 }} />
                </IconButton>
              </Box>
            ))}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{ color: colors.gray700, "&:hover": { bgcolor: colors.gray100 } }}
        >
          {t("evaluationPage.cancel")}
        </Button>
        <Button
          variant="contained"
          sx={{
            borderRadius: 2,
            backgroundColor: colors.accent,
            color: colors.white,
            "&:hover": {
              backgroundColor: colors.orange600,
            },
          }}
          onClick={handleSave}
        >
          {t("evaluationPage.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PageRangesModal;
